import { FC, useCallback, useMemo, useState } from "react";
import { EventStatus } from "@finbackoffice/enums";
import ErrorBoundary from "components/base/error-boundary/ErrorBoundary";
import FadeInAnimation from "components/base/fade-in/FadeInAnimation";
import Loading from "components/base/loading/Loading";
import Translate from "components/base/translate/Translate";
import LiveGameSelector from "components/pages/sport/live-game/game-selector/LiveGameSelector";
import LiveScoreboard from "components/pages/sport/live-game/scoreboard/LiveScoreboard";
import PrematchGameHeader from "components/pages/sport/prematch-game/header/PrematchGameHeader";
import { useGame } from "hooks";
import MarketGroup from "../market-group/MarketGroup";
import PrematchGameSelector from "../../prematch-game/selector/PrematchGameSelector";
import MarketsFilter from "../markets-filter/MarketsFilter";
import styles from "./game.module.sass";

type ViewType = "rowView" | "colView";
export type GameVariantType = "default" | "modal";

type IProps = {
    id: number;
    variant?: GameVariantType;
};

const Game: FC<IProps> = ({ id, variant = "default" }) => {
    const { gameData, addBet, onFavoriteItemClick, favoriteMarketsIds, markets } = useGame(id);

    const [allOpened, setAllOpened] = useState(true);
    const [colType, setColType] = useState<ViewType>("rowView");
    const isLive = useMemo(
        () =>
            gameData &&
            [
                EventStatus.Ended,
                EventStatus.Live,
                EventStatus.Suspended,
                EventStatus.Settled,
            ].indexOf(gameData.status) !== -1,
        [gameData],
    );

    const onCollapseButtonClickHandler = useCallback(() => {
        setAllOpened(!allOpened);
    }, [allOpened]);

    const renderUpperPart = useMemo(() => {
        if (!gameData) return null;

        if (isLive) {
            return (
                <>
                    <ErrorBoundary name={LiveGameSelector.name}>
                        <LiveGameSelector gameData={gameData} variant={variant} />
                    </ErrorBoundary>

                    <LiveScoreboard key={gameData.id} gameData={gameData} />
                </>
            );
        }

        return (
            <>
                <ErrorBoundary name={LiveGameSelector.name}>
                    <PrematchGameSelector selectedGame={gameData} variant={variant} />
                </ErrorBoundary>

                <PrematchGameHeader key={gameData.id} gameData={gameData} variant={variant} />
            </>
        );
    }, [gameData, isLive, variant]);

    if (!gameData) return <Loading />;

    return (
        <FadeInAnimation>
            <div className={styles.gameContainer}>
                {renderUpperPart}
                <ErrorBoundary name={MarketsFilter.name}>
                    <MarketsFilter
                        columnType={colType}
                        columnTypeChange={setColType}
                        onCollapseButtonClick={onCollapseButtonClickHandler}
                        isCollapsed={!allOpened}
                    />
                </ErrorBoundary>

                <ErrorBoundary name="MarketsContainer">
                    {gameData.status === EventStatus.Ended && (
                        <div className="no-game">
                            <Translate tid="game_finished" />
                        </div>
                    )}
                    <>
                        {markets.map((marketGroup) => (
                            <MarketGroup
                                key={marketGroup.id}
                                marketGroup={marketGroup}
                                onOutcomeClickHandler={addBet}
                                forceOpened={allOpened}
                                openedByDefault
                                columnType={colType}
                                onFavoriteItemClick={onFavoriteItemClick}
                                favoriteActive={
                                    favoriteMarketsIds.indexOf(marketGroup.favId) !== -1
                                }
                            />
                        ))}
                    </>
                </ErrorBoundary>
            </div>
        </FadeInAnimation>
    );
};

export default Game;
